import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAllUsersGlobal, updateUser } from '../../services/api'; // user docs contain subscription status
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import toast from 'react-hot-toast';

const ManageSubscriptions = () => {
    const queryClient = useQueryClient();
    const { data: users = [], isLoading } = useQuery({
        queryKey: ['admin-all-users-subs'],
        queryFn: getAllUsersGlobal
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    
    // Form Data for Permissions
    const [formData, setFormData] = useState({
        subscriptionStatus: 'inactive',
        planName: 'free',
        maxEvents: 1,
        allowedTypes: []
    });

    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [profileFormData, setProfileFormData] = useState({});

    const updateMutation = useMutation({
        mutationFn: ({ userId, data }) => updateUser(userId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['admin-all-users-subs']);
            toast.success('Suscripción y permisos actualizados');
            setIsEditModalOpen(false);
        },
        onError: () => toast.error('Error al actualizar')
    });

    const filteredUsers = users.filter(user => 
        user.role === 'organizer' && (
            user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            subscriptionStatus: user.subscriptionStatus || 'trial',
            planName: user.planName || 'organizer',
            maxEvents: user.permissions?.maxEvents || 1,
            allowedTypes: user.permissions?.allowedTypes || ['pozo', 'torneo', 'liga', 'ranking', 'partido'] 
        });
        setIsEditModalOpen(true);
    };

    const openProfileModal = (user) => {
        setSelectedUser(user);
        setProfileFormData({
            phone: user.phone || '',
            gender: user.gender || '',
            level: user.level || 0.5,
            laterality: user.laterality || '',
            position: user.position || '',
            matchesPlayed: user.matchesPlayed || 0
        });
        setIsProfileModalOpen(true);
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        
        // Construct the update object
        const updateData = {
            subscriptionStatus: formData.subscriptionStatus,
            planName: formData.planName,
            permissions: {
                maxEvents: formData.maxEvents === 'unlimited' ? 'unlimited' : Number(formData.maxEvents),
                allowedTypes: formData.allowedTypes
            }
        };

        updateMutation.mutate({ userId: selectedUser.id, data: updateData });
    };

    const handleProfileUpdate = (e) => {
        e.preventDefault();
        updateMutation.mutate({ userId: selectedUser.id, data: profileFormData });
        setIsProfileModalOpen(false);
    };

    const toggleType = (type) => {
        const types = [...formData.allowedTypes];
        if (types.includes(type)) {
            setFormData({ ...formData, allowedTypes: types.filter(t => t !== type) });
        } else {
            setFormData({ ...formData, allowedTypes: [...types, type] });
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Suscripciones y Permisos</h1>
                <p className="text-slate-500 font-medium italic">Control de planes, pagos y límites operativos</p>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                <input 
                    type="text" 
                    placeholder="Buscar organizador..." 
                    className="w-full border-none bg-slate-50 rounded-xl px-4 py-2 focus:ring-2 focus:ring-primary-400 outline-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Organizador</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Estado</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Plan</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Límites</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {filteredUsers.map((user) => (
                            <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-slate-900 text-primary-400 flex items-center justify-center font-black text-xs uppercase shadow-sm">
                                            {user.firstName?.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900 text-sm italic">{user.firstName} {user.lastName}</p>
                                            <p className="text-[10px] text-slate-400">{user.email}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 text-[8px] font-black rounded-full uppercase tracking-tighter ${
                                        user.subscriptionStatus === 'active' ? 'bg-green-100 text-green-700' : 
                                        user.subscriptionStatus === 'past_due' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {user.subscriptionStatus === 'active' ? 'ACTIVO' : user.subscriptionStatus === 'past_due' ? 'IMPAGO' : 'INACTIVO'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="text-xs font-bold text-slate-600 uppercase">{user.planName || 'Free'}</span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-medium text-slate-500">
                                            Max Eventos: <b className="text-slate-900">{user.permissions?.maxEvents === 'unlimited' ? '∞' : user.permissions?.maxEvents || 1}</b>
                                        </span>
                                        <div className="flex gap-1 flex-wrap">
                                            {user.permissions?.allowedTypes?.map(t => (
                                                <span key={t} className="px-1 bg-slate-100 text-[8px] rounded text-slate-500 uppercase">{t}</span>
                                            ))}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <button 
                                        onClick={() => openEditModal(user)}
                                        className="text-xs font-bold text-primary-600 hover:text-primary-800 bg-primary-50 px-3 py-1.5 rounded-lg border border-primary-100 shadow-sm transition-all"
                                    >
                                        Gestionar
                                    </button>
                                    <button 
                                        onClick={() => openProfileModal(user)}
                                        className="text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm transition-all ml-2"
                                    >
                                        Perfil
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl">
                        <h2 className="text-2xl font-black text-slate-900 mb-6 font-display italic">Editar Suscripción</h2>
                        
                        <form onSubmit={handleUpdate} className="space-y-6">
                            
                            {/* Status & Plan */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Estado</label>
                                    <select 
                                        value={formData.subscriptionStatus}
                                        onChange={(e) => setFormData({...formData, subscriptionStatus: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400 font-medium"
                                    >
                                        <option value="trial">Prueba 14 días</option>
                                        <option value="active">Suscrito (Al día)</option>
                                        <option value="blocked">Bloqueado (Impago)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Plan Comercial</label>
                                    <select 
                                        value={formData.planName}
                                        onChange={(e) => setFormData({...formData, planName: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400 font-medium"
                                    >
                                        <option value="organizer">Organizador</option>
                                        <option value="club_manager" disabled>Gestor de Club (Próximamente)</option>
                                        <option value="player">Jugador (Gratuito)</option>
                                    </select>
                                </div>
                            </div>

                            <hr className="border-slate-100" />

                            {/* Permissions */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Límite de Eventos Activos</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl flex-1 border border-transparent hover:border-slate-200">
                                        <input 
                                            type="radio" 
                                            name="maxEvents"
                                            checked={formData.maxEvents === 1}
                                            onChange={() => setFormData({...formData, maxEvents: 1})}
                                            className="text-primary-500 focus:ring-primary-400"
                                        />
                                        <span className="text-sm font-bold text-slate-700">1 Evento</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl flex-1 border border-transparent hover:border-slate-200">
                                        <input 
                                            type="radio" 
                                            name="maxEvents"
                                            checked={formData.maxEvents === 3}
                                            onChange={() => setFormData({...formData, maxEvents: 3})}
                                            className="text-primary-500 focus:ring-primary-400"
                                        />
                                        <span className="text-sm font-bold text-slate-700">3 Eventos</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer bg-slate-50 px-4 py-2 rounded-xl flex-1 border border-transparent hover:border-slate-200">
                                        <input 
                                            type="radio" 
                                            name="maxEvents"
                                            checked={formData.maxEvents === 'unlimited'}
                                            onChange={() => setFormData({...formData, maxEvents: 'unlimited'})}
                                            className="text-primary-500 focus:ring-primary-400"
                                        />
                                        <span className="text-sm font-bold text-slate-700">Ilimitados</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Tipos Permitidos</label>
                                <div className="flex flex-wrap gap-2">
                                    {['pozo', 'torneo', 'liga', 'ranking', 'partido'].map(type => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => toggleType(type)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide transition-all ${
                                                formData.allowedTypes.includes(type)
                                                ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20'
                                                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                            }`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button 
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="px-6 py-3 rounded-xl font-bold bg-slate-900 text-white hover:bg-slate-800 shadow-lg transition-all"
                                >
                                    Guardar Permisos
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Profile Edit Modal */}
            {isProfileModalOpen && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
                        <h2 className="text-2xl font-black text-slate-900 mb-6 font-display italic">Editar Perfil Técnico</h2>
                        <form onSubmit={handleProfileUpdate} className="space-y-4">
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Teléfono</label>
                                    <input 
                                        type="tel" 
                                        value={profileFormData.phone}
                                        onChange={(e) => setProfileFormData({...profileFormData, phone: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                        placeholder="+34..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Partidos Jugados</label>
                                    <input 
                                        type="number" 
                                        value={profileFormData.matchesPlayed}
                                        onChange={(e) => setProfileFormData({...profileFormData, matchesPlayed: Number(e.target.value)})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex justify-between mb-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">Nivel: {Number(profileFormData.level).toFixed(1)}</label>
                                </div>
                                <input 
                                    type="range" 
                                    min="0.5" 
                                    max="7.0" 
                                    step="0.1"
                                    value={profileFormData.level}
                                    onChange={(e) => setProfileFormData({...profileFormData, level: parseFloat(e.target.value)})}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Género</label>
                                    <select 
                                        value={profileFormData.gender}
                                        onChange={(e) => setProfileFormData({...profileFormData, gender: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="male">Hombre</option>
                                        <option value="female">Mujer</option>
                                        <option value="other">Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Mano</label>
                                    <select 
                                        value={profileFormData.laterality}
                                        onChange={(e) => setProfileFormData({...profileFormData, laterality: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="right">Diestro</option>
                                        <option value="left">Zurdo</option>
                                        <option value="ambidextrous">Ambos</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Posición</label>
                                    <select 
                                        value={profileFormData.position}
                                        onChange={(e) => setProfileFormData({...profileFormData, position: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="drive">Drive</option>
                                        <option value="reves">Revés</option>
                                        <option value="both">Indiferente</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button 
                                    type="button"
                                    onClick={() => setIsProfileModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="px-6 py-3 rounded-xl font-bold bg-primary-500 text-white hover:bg-primary-600 shadow-lg shadow-primary-500/30 transition-all"
                                >
                                    Guardar Perfil
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageSubscriptions;
